import { round } from 'lodash'
import { OwnedPlanet, PurchaseAssistSources, Sheet } from '@prometheus/types'

// Where a player could pull cash from, and how much. Shared so the server's
// purchase assist and the client's "Withdraw Cash & Purchase" button agree on
// what is affordable — a button that promises a withdrawal the server would
// refuse is worse than a disabled one.

/**
 * Un-drawn loan capacity on one planet: the loan its current level supports,
 * less whatever has already been drawn.
 */
export function getPlanetLoanAvailable(planet: OwnedPlanet): number {
  const level = planet?.deed?.levels?.[planet.level]
  if (!level) {
    return 0
  }
  const drawn = planet.loan === -1 ? 0 : (planet.deed.levels[planet.loan]?.loan || 0)
  return Math.max(round((level.loan || 0) - drawn, 2), 0)
}

/**
 * Interest rate a planet's loan carries at its current level. Used to draw
 * the cheapest loans first. Planets with no loan sort last.
 */
export function getPlanetLoanRate(planet: OwnedPlanet): number {
  const level = planet?.deed?.levels?.[planet.level]
  if (!level || !level.loan) {
    return Number.MAX_SAFE_INTEGER
  }
  return (level.loan_cost || 0) / level.loan
}

/**
 * Cash that could be pulled out of each account, cheapest for the player
 * first. Bonds are their own savings (no interest owed), the LOC is
 * borrowing, and planet loans are the last resort.
 */
export function getAvailableCashSources(sheet: Sheet, loc_max: number): PurchaseAssistSources {
  const bonds = Math.max(sheet?.bonds?.cash || 0, 0)
  const loc = Math.max(round(loc_max - (sheet?.loc?.cash || 0), 2), 0)
  let planet_loans = 0
  for (const planet of Object.values(sheet?.planets || {})) {
    planet_loans += getPlanetLoanAvailable(planet)
  }
  return { bonds, loc, planet_loans: round(planet_loans, 2) }
}

export function getAvailableCash(sheet: Sheet, loc_max: number): number {
  const { bonds, loc, planet_loans } = getAvailableCashSources(sheet, loc_max)
  return round(bonds + loc + planet_loans, 2)
}
